import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { valueModeLabelMap } from '@backend/features/export/export.interface';
import { ExportTableComponent } from '../../components/export-table/export-table.component';
import { ExportService } from '../../export.service';
import { IExport } from '../../interface/export.interface';

@Component({
  selector: 'app-export-list-screen',
  templateUrl: './export-list-screen.component.html',
  styleUrl: './export-list-screen.component.scss'
})
export class ExportListScreenComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ExportTableComponent) exportTable?: ExportTableComponent;

  selectedExport: (IExport & { isDownloading?: boolean }) | null = null;
  isDrawerOpen = false;
  readonly valueModeLabelMap = valueModeLabelMap;
  isAutoRefreshing = false;

  private autoRefreshSubscription?: Subscription;
  private delayedRefreshTimeoutId?: ReturnType<typeof setTimeout>;
  private tableDataSubscription?: Subscription;
  private readonly autoRefreshIntervalMs = 10_000;
  private readonly delayedRefreshAfterCreateMs = 3_000;
  private shouldStartAutoRefresh = false;

  constructor(
    private readonly exportService: ExportService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    if (this.route.snapshot.queryParamMap.get('poll') === '1') {
      this.shouldStartAutoRefresh = true;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { poll: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  ngAfterViewInit() {
    this.tableDataSubscription = this.exportTable?.dataSource$?.subscribe((rows) => {
      this.autoStopRefreshWhenDone(rows ?? []);
    });

    if (this.shouldStartAutoRefresh) {
      this.startAutoRefresh();
    }
  }

  ngOnDestroy() {
    this.stopAutoRefresh();
    this.tableDataSubscription?.unsubscribe();
  }

  downloadTaxons() {
    this.exportService.downloadTaxons();
  }

  triggerRefresh() {
    this.exportTable?.refresh();
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    this.isAutoRefreshing = true;
    this.triggerRefresh();
    this.delayedRefreshTimeoutId = setTimeout(() => {
      this.triggerRefresh();
    }, this.delayedRefreshAfterCreateMs);
    this.autoRefreshSubscription = interval(this.autoRefreshIntervalMs).subscribe(() => {
      this.triggerRefresh();
    });
  }

  stopAutoRefresh() {
    this.autoRefreshSubscription?.unsubscribe();
    this.autoRefreshSubscription = undefined;
    if (this.delayedRefreshTimeoutId) {
      clearTimeout(this.delayedRefreshTimeoutId);
      this.delayedRefreshTimeoutId = undefined;
    }
    this.isAutoRefreshing = false;
  }

  getDisplayStatus(status: string): string {
    return status === 'STARTING' ? 'RUNNING' : status;
  }

  isExportFinished(exportItem: IExport): boolean {
    return ['COMPLETED', 'PROCESSED'].includes(exportItem.status) || !!exportItem.finishedAt;
  }

  private autoStopRefreshWhenDone(rows: IExport[]) {
    if (!this.isAutoRefreshing || rows.length === 0) {
      return;
    }

    const hasRunningExports = rows.some((item) => !['COMPLETED', 'PROCESSED'].includes(item.status));

    if (!hasRunningExports) {
      this.stopAutoRefresh();
    }
  }

  openExport(exportItem: IExport) {
    this.selectedExport = exportItem;
    this.isDrawerOpen = true;

    void this.exportService
      .getById(exportItem._id)
      .then((details) => {
        if (this.isDrawerOpen && this.selectedExport?._id === exportItem._id) {
          this.selectedExport = details as IExport;
        }
      })
      .catch(() => undefined);
  }

  closeExport() {
    this.isDrawerOpen = false;
  }

  onDrawerOpenChange(isOpen: boolean) {
    this.isDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedExport = null;
    }
  }

  async download(exportItem: IExport & { isDownloading?: boolean }) {
    exportItem.isDownloading = true;

    try {
      await this.exportService.download(exportItem);
    } finally {
      delete exportItem.isDownloading;
    }
  }

  formatExportMode(exportItem: IExport): string {
    const valueMode = exportItem.filter?.valueMode;

    return valueMode ? this.valueModeLabelMap[valueMode] : 'Unknown';
  }

  cleanFilterObject(filter) {
    const cleanFilter = { ...(filter ?? {}) };

    delete cleanFilter._id;

    if (Array.isArray(cleanFilter.towSearchTerms) && cleanFilter.towSearchTerms.length) {
      cleanFilter['Tow routes / IDs'] = cleanFilter.towSearchTerms;
      delete cleanFilter.towSearchTerms;
    }

    if (Array.isArray(cleanFilter.polygon) && cleanFilter.polygon.length) {
      cleanFilter['Polygon coordinates (shown as lat,lng)'] = cleanFilter.polygon.map(
        ([lng, lat]) => [lat, lng],
      );
      delete cleanFilter.polygon;
    }

    return cleanFilter;
  }
}
